import type React from 'react';

import { type SxProps, type Theme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { type PropsWithChildren } from 'react';

import { type PageTitlesTranslation } from '@/assets/locales/pageTitles/pageTitles.translation.en.ts';
import {
  ViewWrapperPaperStyles,
  ViewWrapperStyles,
} from '@/components/viewWrapper/viewWrapper.styles.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import ViewHeader from '@/views/viewHeader/ViewHeader.comp.js';

type Props = PropsWithChildren & {
  button?: React.ReactNode;
  header?: {
    customTitle?: string;
    display?: boolean;
    pageTitle?: keyof PageTitlesTranslation;
  };
  isLoading?: boolean;
  paperSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
};

export const ViewWrapper: React.FC<Props> = props => {
  const { t } = useTranslationWithPrefix('pageTitles');
  const { button, children, header, isLoading, paperSx, sx } = props;

  const pageTitle = header?.pageTitle ? t(header?.pageTitle) : header?.customTitle;

  const mainStyles = { ...ViewWrapperStyles, ...(sx ?? {}) };
  const paperStyles = { ...ViewWrapperPaperStyles, ...(paperSx ?? {}) };

  if (isLoading) {
    return (
      <Container disableGutters fixed maxWidth="xl" sx={mainStyles}>
        <Paper sx={paperStyles}>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <CircularProgress />
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container disableGutters maxWidth="xl" sx={mainStyles}>
      {!header?.display && button && <Box sx={{ pb: 3, textAlign: 'left' }}>{button}</Box>}
      <Paper sx={paperStyles}>
        {header && header.display && <ViewHeader button={button} customTitle={pageTitle} />}
        <Box sx={{ height: '100%', width: '100%' }}>{children}</Box>
      </Paper>
    </Container>
  );
};
